/* Deal Room · Document Review Modal (iter-alignment · P0 Phase 2)
 * ─────────────────────────────────────────────────────────────
 *
 * Opens a full-height sheet-modal for a single document so the
 * reviewer can:
 *   • preview the file in-context (PDF iframe / image / calm
 *     fallback for other MIME types)
 *   • see the full review history (append-only timeline shipped
 *     by the backend in `doc.review_history`)
 *   • take an action:
 *       – "Mark reviewed"          → POST /review
 *           { action: "mark_reviewed" }
 *       – "Request replacement"   → reveals a reason textarea,
 *           then POST /review
 *           { action: "request_replacement", reason }
 *
 * SSOT: reads and writes exclusively against
 *   /api/deals/{deal_id}/documents/{doc_id}/review
 *   /api/deals/{deal_id}/documents/{doc_id}/url
 * — no local mocks, no client-side status inference.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Clock,
  Download,
  FileText,
  History,
  RefreshCcw,
  X,
} from "lucide-react";
import { api } from "../../lib/api";

const STATUS_META = {
  needs_review: {
    label: "Needs Review",
    pill: "bg-amber-50 text-amber-700 ring-amber-200/70",
    dot: "#F59E0B",
  },
  reviewed: {
    label: "Reviewed",
    pill: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",
    dot: "#10B981",
  },
  replacement_requested: {
    label: "Replacement Requested",
    pill: "bg-rose-50 text-rose-700 ring-rose-200/70",
    dot: "#E11D48",
  },
};

const _fmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const _humanSize = (n) => {
  const v = Number(n || 0);
  if (v <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let idx = 0;
  let val = v;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx += 1;
  }
  return `${val.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
};

const _historyKindLabel = (kind) => {
  if (kind === "reviewed") return "Marked reviewed";
  if (kind === "replacement_requested") return "Replacement requested";
  if (kind === "reopened") return "Reopened";
  return kind || "Update";
};

export const DocumentReviewModal = ({
  document: doc,
  dealId,
  onClose,
  onUpdated,
}) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [urlLoading, setUrlLoading] = useState(true);
  const [showReasonBox, setShowReasonBox] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const status = (doc?.review_status || "needs_review").toLowerCase();
  const meta = STATUS_META[status] || STATUS_META.needs_review;

  const isPdf = useMemo(
    () => (doc?.mime || "").toLowerCase().includes("pdf"),
    [doc?.mime]
  );
  const isImage = useMemo(
    () => (doc?.mime || "").toLowerCase().startsWith("image/"),
    [doc?.mime]
  );

  useEffect(() => {
    let cancelled = false;
    if (!doc?.id || !dealId) return;
    setUrlLoading(true);
    setSignedUrl(null);
    api
      .get(`/api/deals/${dealId}/documents/${doc.id}/url`)
      .then((res) => {
        if (cancelled) return;
        setSignedUrl(res?.data?.url || null);
      })
      .catch(() => {
        if (!cancelled) setSignedUrl(null);
      })
      .finally(() => {
        if (!cancelled) setUrlLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doc?.id, dealId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* iter-alignment · iPad viewport fix — background scroll lock.
   *   Prevents the page beneath the drawer from scrolling
   *   while the modal is open (otherwise the operator can
   *   scroll the underlying Deal Room and reach a state
   *   where the drawer's own controls appear off-screen).
   *   Locks BOTH `<html>` (the actual scrolling element in
   *   this app) and `<body>` so programmatic + user scrolls
   *   are both blocked, then restores the previous overflow
   *   on close. */
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  const submit = useCallback(
    async (action) => {
      if (!doc?.id || !dealId) return;
      if (action === "request_replacement") {
        const trimmed = (reason || "").trim();
        if (!trimmed) {
          toast.error("Add a short reason so the owner knows what to fix.");
          return;
        }
      }
      setBusy(true);
      try {
        const body =
          action === "request_replacement"
            ? { action, reason: (reason || "").trim() }
            : { action };
        const res = await api.post(
          `/api/deals/${dealId}/documents/${doc.id}/review`,
          body
        );
        const updated = res?.data;
        toast.success(
          action === "mark_reviewed"
            ? "Marked as reviewed"
            : "Replacement requested"
        );
        setShowReasonBox(false);
        setReason("");
        onUpdated?.(updated);
      } catch (e) {
        const msg =
          e?.response?.data?.detail?.message ||
          e?.response?.data?.message ||
          "Could not save that review action. Please try again.";
        toast.error(msg);
      } finally {
        setBusy(false);
      }
    },
    [doc?.id, dealId, reason, onUpdated]
  );

  if (!doc) return null;

  const history = Array.isArray(doc.review_history) ? doc.review_history : [];

  /* iter-alignment · iPad viewport fix — portal to `document.body`.
   *   The App shell wraps every route in `.cf-page-enter`, whose
   *   `animation: pageEnter 480ms both` leaves a permanent
   *   transform on the wrapper.  A transformed ancestor becomes
   *   the containing block for `position:fixed` descendants, so
   *   the drawer's `fixed inset-0` resolves against the full-
   *   height page wrapper rather than the viewport — the entire
   *   panel then scrolls with the page and the sticky footer
   *   drifts off-screen (the exact iPad symptom the user
   *   reported).  Rendering through a portal to `document.body`
   *   escapes that containing block and lets `fixed` + `100dvh`
   *   pin the drawer to the real viewport.  Same pattern the
   *   Edit-target-dates modal uses. */
  if (typeof document === "undefined") return null;

  const modal = (
    <div
      className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] flex items-stretch justify-end"
      /* iter-alignment · iPad viewport fix — z-index rides above
       *   the sonner Toaster (default z-index 999999999) so
       *   success toasts fired by Mark reviewed / Request
       *   replacement never paint over or intercept clicks on
       *   the sticky footer actions.  Set inline so Tailwind's
       *   JIT doesn't have to generate an arbitrary-value class
       *   this large. */
      style={{ zIndex: 1000000000 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="document-review-title"
      data-testid="document-review-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* Panel — pinned to the dynamic viewport so the sticky
       *   footer never falls below the visible area on iPad
       *   Safari (100vh includes the retracted address bar and
       *   overshoots the real viewport).  `100dvh` follows the
       *   actual visible height; the flex column ensures the
       *   preview shrinks (`min-h-0`) between the sticky header
       *   and the sticky footer instead of pushing the footer
       *   off-screen.  Width holds ~55% of the workspace on
       *   iPad/laptop widths so the underlying Deal Room stays
       *   usable to the left, with a 92% fallback on narrow
       *   viewports and an 800px absolute cap on ultra-wide
       *   displays. */}
      <div
        className="w-full sm:w-[92%] md:w-[55%] lg:w-[54%] xl:w-[52%] max-w-[800px] bg-white shadow-2xl ring-1 ring-slate-200 flex flex-col overflow-hidden"
        style={{ height: "100dvh", maxHeight: "100dvh" }}
        data-testid="document-review-panel"
      >
        {/* Header */}
        <header className="flex items-start gap-3 px-6 py-4 border-b border-slate-200 shrink-0">
          <span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
            <FileText size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">
              Document review
            </p>
            <h3
              id="document-review-title"
              className="mt-0.5 text-[15px] font-bold text-slate-900 tracking-tight truncate"
              data-testid="document-review-title"
            >
              {doc.name || "Document"}
            </h3>
            <p className="mt-0.5 text-[11.5px] text-slate-500 truncate">
              {doc.folder ? `${doc.folder} · ` : ""}
              {doc.uploaded_by_name ? `Uploaded by ${doc.uploaded_by_name}` : ""}
              {doc.size ? ` · ${_humanSize(doc.size)}` : ""}
              {doc.created_at ? ` · ${_fmt(doc.created_at)}` : ""}
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-2 h-[22px] rounded-md ring-1 ${meta.pill} text-[10.5px] font-semibold whitespace-nowrap`}
            data-testid="document-review-status-pill"
            data-status={status}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: meta.dot }}
            />
            {meta.label}
          </span>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="ml-1 w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 flex items-center justify-center"
            aria-label="Close"
            data-testid="document-review-close"
          >
            <X size={16} />
          </button>
        </header>

        {/* Preview */}
        <section
          className="flex-1 min-h-0 bg-slate-50/70 border-b border-slate-200"
          data-testid="document-review-preview"
        >
          {urlLoading ? (
            <div className="h-full flex items-center justify-center text-[12px] text-slate-500">
              Loading preview…
            </div>
          ) : signedUrl && isPdf ? (
            <iframe
              key={signedUrl}
              /* iter-alignment · iPad viewport fix — append
               *   `#view=FitH` so the browser's PDF viewer
               *   defaults to fit-to-width, letting the document
               *   use the panel's available horizontal space
               *   without the operator having to click through
               *   zoom controls first.  Native viewers preserve
               *   their own page/zoom toolbar so no custom
               *   toolbar is added. */
              src={`${signedUrl}#view=FitH`}
              title={doc.name || "Document preview"}
              className="w-full h-full bg-white"
              data-testid="document-review-preview-iframe"
            />
          ) : signedUrl && isImage ? (
            <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
              <img
                src={signedUrl}
                alt={doc.name || "Document preview"}
                className="max-w-full max-h-full object-contain rounded"
                data-testid="document-review-preview-image"
              />
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
              <FileText size={24} className="text-slate-400" />
              <p className="text-[13px] font-semibold text-slate-700" data-testid="document-review-preview-state">
                {signedUrl ? "Preview unavailable for this file type" : "File unavailable"}
              </p>
              {signedUrl ? (
                <a
                  href={signedUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800"
                  data-testid="document-review-download-link"
                >
                  <Download size={12} /> Open in new tab
                </a>
              ) : (
                <p className="text-[11.5px] text-slate-500" data-testid="document-review-file-unavailable">
                  The stored file for this record couldn't be opened.
                </p>
              )}
            </div>
          )}
        </section>

        {/* History */}
        {history.length > 0 && (
          <section
            className="px-6 py-4 border-b border-slate-200 shrink-0"
            data-testid="document-review-history"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <History size={13} className="text-slate-500" />
              <p className="text-[11px] uppercase tracking-[0.10em] font-semibold text-slate-500">
                Review history
              </p>
            </div>
            <ul className="space-y-2 max-h-[140px] overflow-y-auto">
              {[...history].reverse().map((ev, idx) => (
                <li
                  key={`${ev.at}-${idx}`}
                  className="flex items-start gap-2.5 text-[12px]"
                  data-testid={`document-review-history-item-${idx}`}
                >
                  <Clock size={12} className="mt-0.5 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-700">
                      {_historyKindLabel(ev.kind)}
                      <span className="ml-1.5 font-normal text-slate-500">
                        · {ev.by_name || "Unknown"}
                      </span>
                    </p>
                    <p className="text-[11px] text-slate-500">{_fmt(ev.at)}</p>
                    {ev.reason && (
                      <p className="mt-1 text-[12px] text-slate-700 bg-slate-50 rounded px-2 py-1 ring-1 ring-slate-200/70">
                        “{ev.reason}”
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Actions — sticky at the bottom of the panel so the
         *   review buttons remain visible regardless of preview
         *   or history height.  `shrink-0` keeps the footer at
         *   its natural height; `flex-1 min-h-0` on the preview
         *   above shrinks the canvas to fit instead of pushing
         *   this footer below the viewport. */}
        <footer className="px-6 py-4 flex flex-col gap-3 border-t border-slate-200 bg-white shrink-0">
          {showReasonBox && (
            <div>
              <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">
                Reason for replacement
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Explain what&apos;s wrong so the owner can fix it (e.g. wrong signature page, missing schedule 2)…"
                className="w-full px-3 py-2 text-[13px] rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-rose-200 resize-none"
                data-testid="document-review-reason-input"
              />
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            {showReasonBox ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setShowReasonBox(false);
                    setReason("");
                  }}
                  className="h-9 px-3 text-[12.5px] font-semibold text-slate-600 rounded-md hover:bg-slate-100"
                  data-testid="document-review-cancel-replacement-btn"
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => submit("request_replacement")}
                  disabled={busy || !reason.trim()}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 text-[12.5px] font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="document-review-submit-replacement-btn"
                >
                  <RefreshCcw size={12} /> Send request
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowReasonBox(true)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 text-[12.5px] font-semibold text-rose-700 bg-rose-50 ring-1 ring-rose-200 hover:bg-rose-100 rounded-md disabled:opacity-40"
                  data-testid="document-review-request-replacement-btn"
                >
                  <RefreshCcw size={12} /> Request replacement
                </button>
                <button
                  type="button"
                  onClick={() => submit("mark_reviewed")}
                  disabled={busy || !signedUrl}
                  title={!signedUrl && !urlLoading ? "The file can't be opened, so it can't be marked reviewed" : undefined}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 text-[12.5px] font-semibold text-white rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                  }}
                  data-testid="document-review-mark-reviewed-btn"
                >
                  <CheckCircle2 size={12} /> Mark reviewed
                </button>
              </>
            )}
          </div>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DocumentReviewModal;
